import React from 'react';
import CSelect, { Error } from 'src/components/form/Select';
import { ActionMeta, Props, components, OptionProps } from 'react-select';
import _ from 'lodash';
import styled from 'styled-components';
import { roleOptions } from 'src/api/types';
import { Role } from 'src/graphql/_generated';
import { Tooltip } from 'src/common/Tooltip';

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface RoleSelectProps extends Omit<Props, 'value' | 'options'> {
  value: Role | undefined | null;
  onChange: (value: { label: string; value: Role }, meta: ActionMeta<any>) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string | false;
  disabled?: boolean;
  placeholder?: string;
  filteredOutRoles?: Role[];
  disabledOptions?: { value: Role; reason: string }[];
}

interface OptionType {
  label: string;
  value: Role;
  isDisabled?: boolean;
  disabledReason?: string;
}

const CustomOption: React.FC<OptionProps<OptionType, false>> = (props) => {
  const { data, children } = props;

  if (data.isDisabled && data.disabledReason) {
    return (
      <components.Option {...props}>
        <OptionWrapper>
          <span>{children}</span>
          <Tooltip
            dataId={`role-option-${data.value}`}
            dataHtml={data.disabledReason}
            place="right"
            infoIconStyle={{ transform: 'scale(0.8)' }}
          />
        </OptionWrapper>
      </components.Option>
    );
  }

  return <components.Option {...props} />;
};

export const RoleSelect: React.FC<RoleSelectProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
  placeholder = 'Select role',
  filteredOutRoles,
  disabledOptions,
  ...rest
}) => {
  const baseOptions = filteredOutRoles?.length
    ? roleOptions.filter((option) => !filteredOutRoles.includes(option.value))
    : roleOptions;

  const options: OptionType[] = _.orderBy(
    baseOptions.map((option) => {
      const disabledOption = disabledOptions?.find((d) => d.value === option.value);
      return {
        ...option,
        isDisabled: !!disabledOption,
        disabledReason: disabledOption?.reason,
      };
    }),
    [(option) => option.isDisabled, (option) => option.label.toLowerCase()],
    ['asc', 'asc'],
  );

  return (
    <>
      <CSelect
        isDisabled={disabled}
        value={_.find(options, (o) => o.value === value)}
        placeholder={placeholder}
        options={options}
        onChange={onChange}
        onBlur={onBlur}
        inputId="role-select"
        name="role"
        components={{ Option: CustomOption }}
        {...rest}
      />
      {error && helperText && <Error>{helperText}</Error>}
    </>
  );
};
